import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  IonAccordionGroup,
  IonAccordion,
  IonItem,
  IonLabel,
} from "@ionic/react";
import moment from "moment";
import { EstimacionesContext } from "../../context/EstimacionesContext";
import useProyecto from "../../hooks/useProyecto";
import usePreciario from "../../hooks/usePreciario";
import SingleEstimacion from "./SingleEstimacion";
import { ModalContext } from "../../context/ModalContext";
import EstimacionForm from "../finanzas/EstimacionForm";
import StatusBadge from "../common/StatusBadge";
import EmptyState from "../common/EmptyState";
import DynamicSearchSelect from "../common/DynamicSearchSelect";

// Title-case a dashed/spaced value: "conciliada-con-obras" -> "Conciliada Con Obras".
const prettyLabel = (value) =>
  String(value)
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

// Same semantic colors StatusBadge uses, keyed by the estado's first segment so
// compound states (e.g. "conciliada-con-obras") still resolve to a color.
const STATUS_VARIANTS = {
  autorizada: "success",
  liberada: "success",
  enviada: "dark",
  cancelada: "danger",
  creada: "warning",
  revisada: "primary",
  conciliada: "primary",
};

const statusColor = (estado) => {
  const base = String(estado).split("-")[0];
  return `var(--bs-${STATUS_VARIANTS[base] || "primary"})`;
};

// Build distinct {value,label,color?} options from a key across the estimaciones.
const buildOptions = (estimaciones, key, { normalize, colorFn } = {}) => {
  if (!Array.isArray(estimaciones)) return [];
  const seen = new Set();
  const options = [];
  estimaciones.forEach((estimacion) => {
    const value = normalize ? normalize(estimacion[key]) : estimacion[key];
    if (value == null || value === "" || seen.has(value)) return;
    seen.add(value);
    options.push({
      value,
      label: prettyLabel(value),
      ...(colorFn ? { color: colorFn(value) } : {}),
    });
  });
  return options;
};

const normalizeTipo = (tipo) => tipo || "normal";

const Estimaciones = () => {
  const {
    estimacion,
    estimaciones,
    selectEstimacion,
    createEstimacion,
    getEstimacionesContrato,
  } = useContext(EstimacionesContext);
  const { modalComponent } = useContext(ModalContext);
  const { idContrato } = usePreciario();
  const { idProyecto } = useProyecto();

  // Search term, matched server-side against the estimaciones' conceptos.
  const [query, setQuery] = useState("");
  const [estadoFilter, setEstadoFilter] = useState(null);
  const [tipoFilter, setTipoFilter] = useState(null);

  const estadoOptions = useMemo(
    () => buildOptions(estimaciones, "estado", { colorFn: statusColor }),
    [estimaciones]
  );
  const tipoOptions = useMemo(
    () => buildOptions(estimaciones, "tipo", { normalize: normalizeTipo }),
    [estimaciones]
  );

  // Filtering happens locally — a single contract is very unlikely to have more
  // than a couple hundred estimaciones, so there's no need to hit the server.
  const filteredEstimaciones = useMemo(() => {
    if (!Array.isArray(estimaciones)) return estimaciones;
    return estimaciones.filter(
      (item) =>
        (!estadoFilter || item.estado === estadoFilter) &&
        (!tipoFilter || normalizeTipo(item.tipo) === tipoFilter)
    );
  }, [estimaciones, estadoFilter, tipoFilter]);

  // Fetch on mount / contrato change and whenever the search term changes.
  // The query is matched server-side against the estimaciones' conceptos, so it
  // is debounced to avoid a request on every keystroke.
  useEffect(() => {
    const handler = setTimeout(
      () => getEstimacionesContrato(idProyecto, idContrato, query),
      query ? 400 : 0
    );
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idProyecto, idContrato, query]);

  function fetchEstimaciones() {
    getEstimacionesContrato(idProyecto, idContrato, query);
  }

  function handleSelect(idEstimacion) {
    selectEstimacion(idProyecto, idContrato, idEstimacion);
  }

  function handleCreateEstimacion() {
    createEstimacion();
    modalComponent(
      "Agregar Estimacion",
      <EstimacionForm
        idProyecto={idProyecto}
        idContrato={idContrato}
        handleCallback={fetchEstimaciones}
      />
    );
  }

  const renderEstimaciones = () => {
    if (!Array.isArray(estimaciones)) {
      return (
        <div className="d-flex justify-content-center py-4">
          <div className="spinner-border text-secondary" />
        </div>
      );
    }
    if (estimaciones.length === 0) {
      return (
        <EmptyState
          icon="fa fa-file-invoice-dollar"
          message="No hay estimaciones registradas para este contrato."
        />
      );
    }
    if (filteredEstimaciones.length === 0) {
      return (
        <EmptyState
          icon="fa fa-filter"
          message="Ninguna estimación coincide con los filtros."
        />
      );
    }
    return filteredEstimaciones.map(
        ({ tipo, numero, estado, fecha_inicio, responsable, idEstimacion }) => (
          <IonAccordion
            key={idEstimacion}
            value={idEstimacion}
            className="border-0"
          >
            <IonItem
              slot="header"
              color={tipo === "extra" ? "unset" : "light"}
              className={`border-0 ${tipo === "extra" ? "bg-extra" : ""}`}
              onClick={() => {
                handleSelect(idEstimacion);
              }}
            >
              <IonLabel slot="start">
                #{numero} -{" "}
                {moment(fecha_inicio).format("DD MMM YYYY")}
                {tipo === "extra" ? " - Extra" : ""}
                {responsable && responsable !== null && (
                  <span className="d-inline ms-3">
                    <i className="fa fa-user-circle me-2" />
                    {responsable.nombre}
                  </span>
                )}
              </IonLabel>
              <IonLabel slot="end">
                <StatusBadge text={estado} small />
              </IonLabel>
            </IonItem>
            <div slot="content" className="container-fluid py-3 px-0">
              {estimacion === null ||
              estimacion.idEstimacion !== idEstimacion ? (
                <div className="spinner-border" />
              ) : (
                <SingleEstimacion />
              )}
            </div>
          </IonAccordion>
        )
      );
  };

  return (
    <div className="card p-3 shadow">
      <div className="row align-items-center mb-3">
        <div className="col-10">
          <h3 className="h5 mb-0">Estimaciones</h3>
        </div>
        <div className="col-2 text-end">
          <button
            key="agregar"
            onClick={handleCreateEstimacion}
            className="btn btn-outline-primary"
          >
            <i className="fa fa-plus" />
          </button>
        </div>
      </div>
      <div className="row align-items-center mb-3 g-2">
        <div className="col-12 col-md-6">
          <div className="input-group">
            <span className="input-group-text bg-white">
              <i className="fa fa-search text-secondary" />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Buscar estimación..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>
        <div className="col-6 col-md-3">
          <DynamicSearchSelect
            items={estadoOptions}
            value={estadoFilter}
            modifier={setEstadoFilter}
            placeholder="Estatus"
          />
        </div>
        <div className="col-6 col-md-3">
          <DynamicSearchSelect
            items={tipoOptions}
            value={tipoFilter}
            modifier={setTipoFilter}
            placeholder="Tipo"
          />
        </div>
      </div>
      <IonAccordionGroup>{renderEstimaciones()}</IonAccordionGroup>
    </div>
  );
};

export default Estimaciones;
